/**
 * Single source of truth for landing copy + data.
 * Edit here → commit → Vercel redeploys automatically.
 *
 * BLOCKER pre-launch: stats below are placeholders. Replace with real
 * figures from the Brinca/Impacta IA Chile adoption study before go-live.
 */

export const event = {
  name: "Impacta IA",
  edition: "Primera edición · 2026",
  city: "Santiago, Chile",
  monthYear: "Septiembre 2026",
  dates: "2 y 3 de septiembre 2026",
  venue: "Fundación Chile",
  venueAddress: "Av. Parque Antonio Rabat Sur 6165, Vitacura, Santiago",
  format: "2 días · presencial",
  capacity: "400 — exclusivo C-level",
  contact: {
    general: "hola@impactaia.cl",
    sponsors: "sponsors@impactaia.cl",
    press: "prensa@impactaia.cl",
  },
} as const;

// Anclas root-relative (`/#…`) para que la nav funcione también desde las
// páginas legales (/terminos, /privacidad, /codigo-etica): llevan a la home
// y bajan a la sección. En la home son scroll suave sin recargar.
export const navItems = [
  { label: "El evento", href: "/#evento" },
  { label: "Ejes temáticos", href: "/#ejes" },
  { label: "Para quién", href: "/#para-quien" },
  { label: "Speakers", href: "/#speakers" },
  { label: "Formatos", href: "/#formatos" },
] as const;

/**
 * Brinca es el ORGANIZADOR del evento — no un patrocinador. Se trata aparte
 * de `partners` (que son respaldos) para no diluir su rol en la grilla.
 * Fuente única de los datos de marca de Brinca en el sitio.
 *
 * - `logoDark`  → sobre fondos claros (BackedBy, bloque "Detrás de…").
 * - `logoLight` → sobre fondos oscuros (footer).
 * - `url`       → con UTM para atribuir el tráfico que Impacta IA deriva a Brinca.
 *
 * Copy y datos verificados en https://www.brinca.com (jun 2026).
 */
export const organizer = {
  name: "Brinca",
  url: "https://www.brinca.com/?utm_source=impactaia&utm_medium=referral&utm_campaign=impacta-ia-2026",
  logoDark: "/img/partners/brinca.svg",
  logoLight: "/img/partners/brinca-white.svg",
  width: 136,
  height: 37,
  blurb:
    "Impacta IA nace de la alianza estratégica entre Brinca y Chile Global Ventures, el área de venture capital e innovación abierta de Fundación Chile. Unimos la consultoría estratégica en gestión del cambio e inteligencia artificial con el ecosistema de inversión y emprendimiento más relevante de la región. En conjunto, acompañamos a grandes organizaciones a acelerar su transformación mediante I+D, corporate venturing, cultura digital e IA aplicada.",
  pillars: ["IA Aplicada", "Corporate Venturing", "Gestión del Cambio", "Innovación Abierta", "Estrategia"] as const,
  stats: [
    { num: "+15", lbl: "Años liderando la innovación y transformación corporativa en Chile y Latam." },
    { num: "+80", lbl: "Grandes empresas acompañadas en estrategia, I+D, cultura e inteligencia artificial." },
    { num: "2", lbl: "Ecosistemas conectados: consultoría estratégica y venture capital." },
  ],
  organizations: [
    {
      name: "Brinca",
      logo: "/img/partners/brinca.svg",
      width: 136,
      height: 37,
      url: "https://www.brinca.com/?utm_source=impactaia&utm_medium=referral&utm_campaign=impacta-ia-2026",
      linkLabel: "Conoce Brinca",
    },
    {
      name: "Chile Global Ventures",
      logo: "/img/partners/chile-global-ventures-fch50.png",
      width: 274,
      height: 80,
      url: "https://chileglobalventures.cl/",
      linkLabel: "Conoce CGV",
    },
  ],
} as const;

/**
 * Logos de partners agrupados por rol, tal como aparecen en la sección
 * "BackedBy". Cada logo declara su tamaño intrínseco; el layout normaliza por
 * altura. Archivos en /public/img/partners/.
 */
export type PartnerLogo = {
  readonly name: string;
  readonly logo: string;
  readonly width: number;
  readonly height: number;
  /** Sobrescribe el tamaño por defecto del logo (clases Tailwind de altura). */
  readonly sizeClass?: string;
};

export type PartnerGroup = {
  readonly label: string;
  /** "attribution" → etiqueta centrada en minúscula (ej. CORFO). */
  readonly variant?: "attribution";
  /** Oculta la etiqueta (p.ej. cuando el texto ya viene dentro del logo). */
  readonly hideLabel?: boolean;
  readonly logos: readonly PartnerLogo[];
};

export const partnerGroups: readonly PartnerGroup[] = [
  {
    label: "Organizan",
    logos: [
      { name: "Brinca", logo: "/img/partners/brinca.svg", width: 136, height: 37, sizeClass: "h-8 sm:h-11" },
      { name: "Chile Global Ventures y Fundación Chile", logo: "/img/partners/chile-global-ventures-fch50.png", width: 274, height: 80, sizeClass: "h-8 sm:h-11" },
      { name: "CORFO", logo: "/img/partners/corfo-v2.png", width: 276, height: 125, sizeClass: "h-9 sm:h-14" },
    ],
  },
  {
    label: "Colaboran",
    logos: [
      { name: "Universidad Adolfo Ibáñez", logo: "/img/partners/uai.png", width: 360, height: 82 },
      { name: "DEC", logo: "/img/partners/dec.png", width: 375, height: 190, sizeClass: "h-11 sm:h-14" },
    ],
  },
  {
    label: "Media partner",
    logos: [{ name: "Diario Financiero", logo: "/img/partners/diario-financiero.png", width: 98, height: 85 }],
  },
];

/** Sponsors del evento. Gold comparte jerarquía visual; Silver se presenta a menor escala. */
export const sponsorGroups: readonly PartnerGroup[] = [
  {
    label: "Sponsor Gold",
    logos: [
      { name: "BUK", logo: "/img/partners/buk.png", width: 1101, height: 498, sizeClass: "h-10 sm:h-12" },
      { name: "SimpleTech", logo: "/img/partners/simpletech.png", width: 1819, height: 355, sizeClass: "h-10 sm:h-12 max-w-[160px] sm:max-w-[200px] translate-y-1.5" },
      { name: "Softtek", logo: "/img/partners/softtek-logo.png", width: 960, height: 540, sizeClass: "h-14 sm:h-16" },
      { name: "Vambe", logo: "/img/partners/vambe.webp", width: 1600, height: 515, sizeClass: "h-10 sm:h-12" },
      { name: "LinkCircular", logo: "/img/partners/linkcircular.png", width: 861, height: 135, sizeClass: "h-7 sm:h-8" },
    ],
  },
  {
    label: "Sponsor Silver",
    logos: [
      { name: "CCU", logo: "/img/partners/ccu.png", width: 1470, height: 683, sizeClass: "h-10 sm:h-12" },
      { name: "Wordly", logo: "/img/partners/wordly.jpg", width: 514, height: 197, sizeClass: "h-10 sm:h-12" },
    ],
  },
];

// PLACEHOLDER — replace with real data from the Chile AI adoption study
export const problemaStats = [
  { num: "37%",   lbl: "de empresas chilenas usa IA de forma sistemática", color: "var(--color-blue-500)" },
  { num: "1 de 5", lbl: "proyectos de IA llega a producción",               color: "var(--color-mint-500)" },
  { num: "84%",   lbl: "de los CEO ve la IA como prioridad estratégica",   color: "var(--color-pink-500)" },
] as const;

export const ejes = [
  {
    id: "innovar",
    n: "01",
    title: "Innovar",
    sub: "Emprender con IA",
    desc:
      "Tendencias globales, expertos internacionales, startups del ecosistema y talleres de vibe-coding para transformar los desafíos en nuevas oportunidades de negocio.",
    icon: "/img/icon-innovar-v2.png",
    color: "#B11362",
    tint: "var(--color-pink-100)",
    tags: ["Startups IA", "Vibe-coding", "Producto"],
  },
  {
    id: "adoptar",
    n: "02",
    title: "Adoptar",
    sub: "IA en la empresa",
    desc:
      "Gobernanza corporativa, futuro del trabajo y casos reales que muestran cómo pasar de iniciativas de IA fragmentadas a una adopción estratégica que se traduce en impacto real en la rentabilidad del negocio.",
    icon: "/img/icon-adoptar-v2.png",
    color: "var(--color-blue-500)",
    tint: "var(--color-blue-100)",
    tags: ["Casos reales", "ROI por función", "Change management"],
  },
  {
    id: "escalar",
    n: "03",
    title: "Escalar",
    sub: "IA con I+D",
    desc:
      "Cómo llevar la IA del piloto a la operación y convertirla en ventaja competitiva a través de rediseño de procesos, eficiencia operativa e infraestructura que escala el crecimiento.",
    icon: "/img/icon-escalar-v2.png",
    color: "#0F8A75",
    tint: "var(--color-mint-100)",
    tags: ["Supercómputo", "Ley I+D", "Datasets"],
  },
] as const;

export const profiles = [
  {
    role: "CEO / Gerente General",
    what: "Estrategia",
    desc: "Cuándo, cuánto y cómo invertir en IA. Casos de empresas chilenas que ya están midiendo retorno.",
  },
  {
    role: "CTO / CIO / CDO",
    what: "Implementación",
    desc: "Arquitecturas, equipos y operación. Del piloto a producción sin morir en el intento.",
  },
  {
    role: "Founder / Emprendedor",
    what: "Construir con IA",
    desc: "Vibe-coding, MVPs en semanas y la mecánica de escalar startups nativas IA en 2026.",
  },
  {
    role: "Gerente de Personas / Transformación",
    what: "Cultura y adopción",
    desc: "Cómo lograr que la IA se use de verdad. Reskilling, change management y el rol de RRHH como motor de la transformación.",
  },
  {
    role: "Director de Innovación",
    what: "Adopción a escala",
    desc: "Gobernanza, portafolio y la conversación honesta con áreas de negocio.",
  },
] as const;

/**
 * Speakers por día. `photo: null` → placeholder gris (aún sin foto).
 * Drop fotos en /public/img/speakers/ y apunta `photo` cuando lleguen.
 */
export type Speaker = {
  readonly name: string;
  readonly role: string;
  readonly photo: string | null;
  /** Ajuste de encuadre puntual para imágenes con borde o composición irregular. */
  readonly photoClass?: string;
  readonly country: string;
  /** Se muestra en el adelanto de speakers de la home. */
  readonly featured?: boolean;
  /** Institución de origen — se muestra junto a la bandera en la tarjeta. */
  readonly institucion?: string;
  /** Tema/track de la charla — SOLO para el filtro de /speakers (ya no se
      muestra como tag en la tarjeta). */
  readonly tema?: SpeakerTema;
  /**
   * URL de LinkedIn del orador. Opcional a nivel de tipo, pero SIEMPRE hay que
   * completarlo al agregar un speaker: sin URL la tarjeta no muestra el ícono.
   * Para gente de Brinca, https://www.brinca.com/nosotros/ lista los perfiles.
   * Normalizar a https://www.linkedin.com/in/<slug>/ (no-ASCII percent-encoded).
   */
  readonly linkedin?: string;
  /**
   * Oculta el perfil de la web (home y /speakers) sin borrar los datos.
   * Uso temporal: mientras se corrige o valida la ficha del orador.
   */
  readonly oculto?: boolean;
};

/** Temas (tracks) de las charlas — alineados a los tres ejes del evento. */
export const speakerTemas = ["Innovación", "Adopción", "Escalamiento"] as const;
export type SpeakerTema = (typeof speakerTemas)[number];

/** Banderas circulares por país (en /public/img/flags/). */
export const countryFlags: Record<string, string> = {
  Argentina: "/img/flags/ar.svg",
  Italia: "/img/flags/it.svg",
  "Reino Unido": "/img/flags/gb.svg",
  Chile: "/img/flags/cl.svg",
  USA: "/img/flags/us.svg",
  Perú: "/img/flags/pe.svg",
};


/**
 * Speakers en orden de despliegue. Foto = tarjeta cuadrada B/N + nodos
 * en /public/img/speakers/<slug>.webp. `photo: null` → placeholder de marca.
 * Lista única (sin división por día hasta tener el día real de cada charla).
 *
 * El orden está calibrado para el grid de 4 columnas: una oradora por fila,
 * escalonada para que no caiga siempre en la misma columna (3.ª, 4.ª, 2.ª,
 * 1.ª → posiciones 3, 8, 10 y 13). Eso además evita que dos queden en la
 * misma fila del grid de 2 columnas (sm). Al agregar o quitar speakers,
 * mantener una por fila y rotar la columna.
 */
const speakersData: readonly Speaker[] = [
  {
    name: "Stefano Puntoni",
    role: "Experto global en IA, negocios y ciencia del comportamiento. Investiga cómo la automatización y la IA generativa transforman la productividad corporativa.",
    photo: "/img/speakers/speaker-1.webp",
    country: "USA",
    institucion: "Wharton, Penn U.",
    tema: "Innovación",
    featured: true,
    linkedin: "https://www.linkedin.com/in/stefanopuntoni/",
  },
  {
    name: "Daniel Strode",
    role: "Experto en IA y RR.HH. y profesor de Duke University. Asesora a empresas globales en la integración de IA y talento. Autor de The Quiet Reckoning, una guía para liderar la transformación digital y el futuro del trabajo.",
    photo: "/img/speakers/speaker-2.webp",
    country: "USA",
    institucion: "Duke University",
    tema: "Innovación",
    featured: true,
    linkedin: "https://www.linkedin.com/in/danstrode/",
  },
  {
    name: "Denisse Goldfarb",
    role: "CEO de The People Future; experta en Futuro del Trabajo, Talento y Cultura Organizacional. Ex CPO de Walmart y Microsoft Chile.",
    photo: "/img/speakers/new-denisse-goldfarb.webp",
    country: "Chile",
    institucion: "The People Future",
    tema: "Adopción",
    featured: true,
    linkedin: "https://www.linkedin.com/in/denissegoldfarb/",
  },
  {
    name: "Ignacio Briones",
    role: "Economista, profesor de la Universidad Adolfo Ibáñez, Presidente Horizontal y ex Ministro de Hacienda. Especialista en políticas públicas, productividad y desarrollo económico.",
    photo: "/img/speakers/ignacio-briones.webp",
    country: "Chile",
    institucion: "UAI, Horizontal",
    tema: "Escalamiento",
    featured: true,
    linkedin: "https://www.linkedin.com/in/ignacio-briones-rojas/",
  },
  {
    name: "Francisco Martínez",
    role: "Gerente de Estrategia, Innovación y Cultura, y cofundador de Brinca. Vocero oficial de Impacta IA 2026.",
    photo: "/img/speakers/francisco-martinez.webp",
    country: "Chile",
    institucion: "Brinca",
    tema: "Adopción",
    featured: true,
    linkedin: "https://www.linkedin.com/in/fmartinezt/",
  },
  {
    name: "Macarena McKay",
    role: "Directora de la Asociación de Ética Empresarial y Organizacional de Chile; experta en ética y gobernanza de la IA. Directora Ejecutiva de Fundación Economía y Administración Alumni UC y una de las 100 Mujeres Líderes 2025.",
    photo: "/img/speakers/macarena-mckay.webp",
    country: "Chile",
    institucion: "Ética Empresarial Chile",
    tema: "Adopción",
    featured: true,
    linkedin: "https://www.linkedin.com/in/macarenamckay/",
  },
  {
    name: "Paula Aguirre",
    role: "Vicerrectora de Inteligencia Digital, PUC. Su carrera ha estado marcada por una mirada interdisciplinaria desde astronomía a inteligencia artificial. Un perfil que la llevó a ser designada para encabezar la \"Iniciativa de Innovación Digital, Datos y Tecnología\", y luego la vicerrectoría UC.",
    photo: "/img/speakers/paula-aguirre.webp",
    country: "Chile",
    institucion: "PUC",
    tema: "Adopción",
    featured: true,
  },
  {
    name: "Alfredo Pérsico",
    role: "CEO & Founder de Agents4Future. Especialista en escenarios tecnológicos, mentalidades de cambio y modelos de impacto humano-tecnológico.",
    photo: "/img/speakers/alfredo-persico.webp",
    country: "Perú",
    institucion: "Agents4Future",
    tema: "Innovación",
    linkedin: "https://www.linkedin.com/in/alfredo-p%C3%A9rsico-guti%C3%A9rrez-38852486/",
  },
  {
    name: "Julio Pertuzé",
    role: "Presidente de Fundación Chile; Profesor de la Escuela de Ingeniería UC. Vocero Oficial Impacta IA 2026.",
    photo: "/img/speakers/julio-pertuze.webp",
    country: "Chile",
    institucion: "Fundación Chile",
    tema: "Escalamiento",
    linkedin: "https://www.linkedin.com/in/julio-pertuz%C3%A9-1458124/",
  },
  {
    name: "Claudia Montoya",
    role: "Directora de Empresas, Mentora Directiva y Consultora en IA. Integra el Comité de Tecnología y Transformación Digital de REDMAD.",
    photo: "/img/speakers/claudia-montoya.webp",
    country: "Chile",
    institucion: "REDMAD",
    tema: "Adopción",
    linkedin: "https://www.linkedin.com/in/claudiahelenamontoyaruiz/",
  },
  {
    name: "Eduardo Gomien",
    role: "Principal Venture Capital & Private Equity en Chile Global Ventures; Fundador y CEO de Chile Nación Emprendedora.",
    photo: "/img/speakers/eduardo-gomien.webp",
    country: "Chile",
    institucion: "Chile Global Ventures",
    tema: "Escalamiento",
    linkedin: "https://www.linkedin.com/in/egomien/",
  },
  {
    name: "Alberto Mayol",
    role: "Sociólogo, Escritor y Analista Político-Social.",
    photo: "/img/speakers/alberto-mayol.webp",
    country: "Chile",
    institucion: "Chile",
    tema: "Innovación",
  },
  {
    name: "Raphael Bergoeing",
    role: "Economista y profesor de la Escuela de Negocios UAI; ex presidente de la Comisión Nacional de Evaluación y Productividad. Experto en productividad e impacto de la IA.",
    photo: "/img/speakers/raphael-bergoeing.webp",
    country: "Chile",
    institucion: "UAI",
    tema: "Escalamiento",
    linkedin: "https://www.linkedin.com/in/raphael-bergoeing-557238a5/",
  },
  {
    name: "Juviska Meza",
    role: "Directora de Gestión Tecnológica; experta en I+D+i y financiamiento público.",
    photo: "/img/speakers/juviska-meza.webp",
    country: "Chile",
    institucion: "Gestión Tecnológica",
    tema: "Escalamiento",
    linkedin: "https://www.linkedin.com/in/juviskameza/",
  },
  {
    name: "Carlos Araya",
    role: "CFO/CTO en ANASAC. Lidera transformaciones donde convergen estrategia financiera, tecnología e innovación.",
    photo: "/img/speakers/carlos-araya.webp",
    country: "Chile",
    institucion: "ANASAC",
    tema: "Adopción",
    linkedin: "https://www.linkedin.com/in/cfaraya/",
  },
  {
    name: "José Manuel Morales",
    role: "CEO & Cofundador de Brinca; Profesor de Innovación y Sostenibilidad UAI. Vocero Oficial Impacta IA 2026.",
    photo: "/img/speakers/jose-manuel-morales.webp",
    country: "Chile",
    institucion: "Brinca",
    tema: "Innovación",
    linkedin: "https://www.linkedin.com/in/jos%C3%A9-manuel-morales-larrazabal-01489a54/",
  },
  {
    name: "Andrés Blake",
    role: "CEO de UPSKILL OTEC y Director de Proyectos en Brinca. Especialista en adopción de IA y desarrollo de capacidades.",
    photo: "/img/speakers/andres-blake.webp",
    country: "Chile",
    institucion: "UPSKILL · Brinca",
    tema: "Adopción",
    linkedin: "https://www.linkedin.com/in/andresblakepavez/",
  },
  {
    name: "Nicolás Rivas",
    role: "Gerente de Soluciones IA en Brinca. Líder de datos, inteligencia de negocios e inteligencia artificial en startups.",
    photo: "/img/speakers/nicolas-rivas.webp",
    country: "Chile",
    institucion: "Brinca",
    tema: "Adopción",
    linkedin: "https://www.linkedin.com/in/nicolasrivasabud/",
  },
  {
    name: "Macarena Catalán",
    role: "Digital Products & UX y Jefa de Salud Digital en Bupa Chile. Experta en product management, salud digital e integración de IA en procesos omnicanal.",
    photo: "/img/speakers/macarena-catalan.webp",
    country: "Chile",
    institucion: "Bupa Chile",
    tema: "Adopción",
    linkedin: "https://cl.linkedin.com/in/mcatalanc",
  },
  {
    name: "Cristian Ludwig",
    role: "Gerente de Inteligencia Artificial en Banco BCI. Especializado en estrategia e implementación de IA generativa, analítica avanzada, transformación tecnológica y rediseño de procesos del sector financiero.",
    photo: "/img/speakers/christian-ludwig.webp",
    country: "Chile",
    institucion: "Banco BCI",
    tema: "Adopción",
    linkedin: "https://www.linkedin.com/in/cristianlvc/",
  },
  {
    name: "Marisa Cozak",
    role: "Directora Ejecutiva de Asociación DEC Chile. Experta en transformación organizacional, cultura de servicio y experiencia de clientes.",
    photo: "/img/speakers/marisa-cozak.webp",
    country: "Chile",
    institucion: "Asociación DEC Chile",
    tema: "Adopción",
    linkedin: "https://cl.linkedin.com/in/marisacozak",
  },
  {
    name: "Fernando Hentzschel",
    role: "Head of Technological Capabilities en CORFO. Especialista en tecnologías limpias (+) 15 años de experiencia pública y privada; hidrógeno verde, electromovilidad y minería.",
    photo: "/img/speakers/fernando-hentzschel.webp",
    country: "Chile",
    institucion: "CORFO",
    tema: "Escalamiento",
    linkedin: "https://cl.linkedin.com/in/fernando-hentzschel",
  },
  {
    name: "Matías Jory",
    role: "Director for Industrial Competitiveness en CMPC. Experto en transformación digital e integración de IA, analítica avanzada e Industria 4.0 en entornos industriales.",
    photo: "/img/speakers/matias-jory.webp",
    country: "Chile",
    institucion: "CMPC",
    tema: "Escalamiento",
    linkedin: "https://www.linkedin.com/in/matias-jory-06b42417/",
  },
  {
    name: "Marcos Thomas",
    role: "CEO y Fundador de Tesserix Capital, Director de Innovación en Brinca y Ex SVP de Innovación y Alianzas Estratégicas en Banco Santander.",
    photo: "/img/speakers/marcos-thomas.webp",
    country: "Chile",
    institucion: "Tesserix Capital",
    tema: "Escalamiento",
    linkedin: "https://www.linkedin.com/in/marcos-b-thomas/",
  },
  {
    name: "Edmundo Casas",
    role: "Emprendedor y líder en inteligencia artificial y transformación digital. Cofundador de Kauel, con trayectoria en soluciones tecnológicas y ecosistemas de innovación.",
    photo: "/img/speakers/edmundo-casas.webp",
    country: "Chile",
    institucion: "Kauel",
    tema: "Innovación",
    linkedin: "https://www.linkedin.com/in/edmundocasas",
  },
  {
    name: "José Rafael Correa",
    role: "Vicerrector de Tecnologías de la Información de la Universidad de Chile. Investigador en sistemas de información, optimización y redes para la toma de decisiones.",
    photo: "/img/speakers/jose-rafael-correa.webp",
    country: "Chile",
    institucion: "Universidad de Chile",
    tema: "Innovación",
  },
  {
    name: "Ángel Izurieta",
    role: "VP de Huawei Cloud para Latinoamérica y ex CEO de Google Chile. Ejecutivo especializado en estrategia digital, IA, cloud y transformación de negocios.",
    photo: "/img/speakers/angel-izurieta.webp",
    country: "Chile",
    institucion: "Huawei Cloud",
    tema: "Innovación",
    linkedin: "https://cl.linkedin.com/in/angelizurieta/es",
  },
  {
    name: "Sebastián Acevedo",
    role: "VP Business & Finance de ChileGlobal Ventures. Impulsa inversión, corporate venturing y conexiones entre startups, empresas y capital.",
    photo: "/img/speakers/sebastian-acevedo.webp",
    country: "Chile",
    institucion: "ChileGlobal Ventures",
    tema: "Escalamiento",
    linkedin: "https://www.linkedin.com/in/sebastian-acevedo/",
  },
  {
    name: "Marcos Orchard",
    role: "Profesor de Ingeniería Eléctrica de la Universidad de Chile y Fellow de la PHM Society. Experto en analítica predictiva y diagnóstico de fallas para baterías, minería y finanzas.",
    photo: "/img/speakers/marcos-orchard.webp",
    country: "Chile",
    institucion: "Universidad de Chile",
    tema: "Innovación",
    linkedin: "https://cl.linkedin.com/in/marcos-orchard-a400681a",
  },
  {
    name: "Pedro Huichalaf",
    role: "Abogado y consultor en tecnología y ciberseguridad; ex Subsecretario de Telecomunicaciones. Especialista en regulación digital, datos personales y políticas públicas tecnológicas.",
    photo: "/img/speakers/pedro-huichalaf.webp",
    photoClass: "scale-[1.03]",
    country: "Chile",
    institucion: "Consultor independiente",
    tema: "Adopción",
    linkedin: "https://cl.linkedin.com/in/pedrohuichalaf",
  },
  {
    name: "George Lever",
    role: "Especialista en economía digital, e-commerce y productividad. Ex Gerente de Estudios de la Cámara de Comercio de Santiago.",
    photo: "/img/speakers/george-lever.webp",
    country: "Chile",
    institucion: "Cámara de Comercio de Santiago",
    tema: "Escalamiento",
    linkedin: "https://cl.linkedin.com/in/georgelever",
  },
  {
    name: "Claudia Ramos",
    role: "Gerente de la División de Innovación y Data & Analytics en Banco Bci.",
    photo: "/img/speakers/claudia-ramos.webp",
    country: "Chile",
    institucion: "Banco Bci",
    tema: "Adopción",
    linkedin: "https://cl.linkedin.com/in/claudia-ramos-0b547832",
  },
  {
    name: "Julio Riutort",
    role: "Director Académico del EMBA de la UAI y profesor asociado de Finanzas. Especialista en financiamiento, venture capital y decisiones estratégicas de inversión.",
    photo: "/img/speakers/julio-riutort.webp",
    country: "Chile",
    institucion: "Universidad Adolfo Ibáñez",
    tema: "Escalamiento",
    linkedin: "https://www.linkedin.com/in/julio-riutort-bb309125",
  },
  {
    name: "Oscar Vidal",
    role: "Líder Académico y de Calidad en Talento Digital para Chile. Experto en formación para el trabajo, educación técnico-profesional y capacidades digitales.",
    photo: "/img/speakers/oscar-vidal.webp",
    country: "Chile",
    institucion: "Talento Digital para Chile",
    tema: "Adopción",
    linkedin: "https://cl.linkedin.com/in/oscar-vidal-aravena-b1596171",
  },
  {
    name: "Arturo Labbé",
    role: "Director de Corporate Venturing de ChileGlobal Ventures. Especialista en innovación abierta, venture client y alianzas entre corporativos y startups.",
    photo: "/img/speakers/arturo-labbe.webp",
    country: "Chile",
    institucion: "ChileGlobal Ventures",
    tema: "Escalamiento",
    linkedin: "https://cl.linkedin.com/in/arturo-labbe-moreno",
  },
];

/**
 * Line-up publicado. Excluye los perfiles marcados con `oculto: true`
 * (datos en revisión), de modo que la home, el conteo y /speakers queden
 * siempre consistentes entre sí.
 */
export const speakers: readonly Speaker[] = speakersData.filter((s) => !s.oculto);



export const numeros = [
  { num: 400, suffix: "",  lbl: "ejecutivos C-level", note: "Curados, exclusivo" },
  { num: 12,  suffix: "+", lbl: "industrias",         note: "Banca, retail, salud, minería..." },
  { num: 3,   suffix: "",  lbl: "ejes temáticos",     note: "Adoptar · Escalar · Innovar" },
  { num: 2,   suffix: "",  lbl: "días de inmersión",  note: "2 y 3 de septiembre" },
] as const;

/**
 * Agenda por día. Columnas = día; filas = franja (AM / PM). Cada bloque
 * declara su color de acento (hex, para derivar tintes con alfa) y el color de
 * texto que va sobre el pill "BLOQUE 0X".
 */
export type AgendaFranja = "AM" | "PM";

export type AgendaBlock = {
  readonly n: string;
  readonly franja: AgendaFranja;
  /** Acento del bloque (hex) — borde, flechas, pill y "Ver detalle". */
  readonly accent: string;
  /** Color de texto sobre el pill de color sólido. */
  readonly onAccent: string;
  readonly title: string;
  readonly points: readonly string[];
};

export type AgendaDay = {
  readonly day: string;
  readonly date: string;
  /** Bloques en orden [AM, PM]. */
  readonly blocks: readonly AgendaBlock[];
};

export const agendaFranjas = [
  { key: "AM", label: "AM · Mañana" },
  { key: "PM", label: "PM · Tarde" },
] as const;

export const agenda: readonly AgendaDay[] = [
  {
    day: "Día 1",
    date: "Miércoles 02 Sep",
    blocks: [
      {
        n: "01",
        franja: "AM",
        accent: "#ED1E79",
        onAccent: "#ffffff",
        title: "IA como decisión estratégica: del gobierno corporativo al retorno",
        points: [
          "Gobierno corporativo, ética y transparencia desde la alta dirección.",
          "Del piloto al impacto: rentabilidad y el costo de no actuar.",
          "Dimensión público-privada: IA en el Estado y competitividad.",
        ],
      },
      {
        n: "02",
        franja: "PM",
        accent: "#ED1E79",
        onAccent: "#ffffff",
        title: "Personas al centro: liderar la transformación cultural con IA",
        points: [
          "El futuro del trabajo con IA: impacto en las personas y gestión del cambio.",
          "Inteligencia Aumentada: multiplicador del talento humano.",
          "Herramientas de IA para RR.HH. y una adopción responsable.",
        ],
      },
    ],
  },
  {
    day: "Día 2",
    date: "Jueves 03 Sep",
    blocks: [
      {
        n: "03",
        franja: "AM",
        accent: "#1DD2B3",
        onAccent: "#0E0E10",
        title: "Arquitectura del futuro: escalar la IA con gobernanza y seguridad",
        points: [
          "Nuevos paradigmas TI: el futuro del SaaS y la IA como motor de productividad.",
          "Gobernanza, seguridad y mitigación de riesgos en la IA agéntica.",
          "Del dato al valor: calidad y conectividad de la información.",
        ],
      },
      {
        n: "04",
        franja: "PM",
        accent: "#1DD2B3",
        onAccent: "#0E0E10",
        title: "IA en la operación: rediseñar procesos y medir el impacto",
        points: [
          "Rediseño y automatización de procesos con IA: casos reales de impacto.",
          "Medición de impacto y fuerza de trabajo híbrida (humanos + IA).",
          "Agentes de IA y No-Code. Encuentro con startups para desafíos concretos.",
        ],
      },
    ],
  },
] as const;

/** Los 4 tipos de actividad del evento — textos finales (jul 2026). */
export const programa = [
  { tag: "Charlas",    title: "Charlas Magistrales",               desc: "Referentes de vanguardia exponen visiones y tendencias, inspirando a la audiencia a redefinir sus límites tecnológicos.",              img: "/img/Group-1.webp" },
  { tag: "Paneles",    title: "Paneles de Expertos",               desc: "Líderes y expertos debaten sobre tendencias y desafíos, compartiendo mejores prácticas desde la experiencia.",                          img: "/img/Group-2.webp" },
  { tag: "Talleres",   title: "Talleres y Resolución de Desafíos", desc: "Espacios prácticos y hands-on donde líderes, expertos y startups trabajan codo a codo sobre desafíos reales del negocio.",              img: "/img/Group-3.webp" },
  { tag: "Networking", title: "Matchmaking y Networking",          desc: "Encuentros diseñados para conectar tomadores de decisión, referentes y soluciones del ecosistema.",                                     img: "/img/Group-4.webp" },
] as const;

export const formInteresOptions = ["Asistente", "Speaker", "Sponsor", "Media"] as const;
export type FormInteres = (typeof formInteresOptions)[number];

/**
 * Días del evento — fuente única para el formulario, los correos de
 * confirmación, los .ics de calendario y el panel admin. `date` debe calzar
 * con las filas de `event_days` en la DB (migración 20260716).
 */
export const eventDays = [
  { key: "sep2", date: "2026-09-02", label: "Miércoles 2 de septiembre", short: "2 sep" },
  { key: "sep3", date: "2026-09-03", label: "Jueves 3 de septiembre", short: "3 sep" },
] as const;
export type EventDayKey = (typeof eventDays)[number]["key"];

/** Horario de cada jornada (hora de Santiago) — usado por los eventos de calendario. */
export const eventSchedule = {
  start: "09:00",
  end: "18:00",
  timezone: "America/Santiago",
} as const;

/** Opción que dispara un campo de texto abierto en el formulario. */
export const NIVEL_OTRO = "Otro";

/** Opciones del dropdown "Nivel de responsabilidad". */
export const formNivelOptions = [
  "Gerente General / CEO",
  "Director(a)",
  "Gerente",
  "Subgerente",
  "Jefatura / Líder",
  "Coordinador(a)",
  "Profesional / Especialista",
  "Consultor(a)",
  NIVEL_OTRO,
] as const;
export type FormNivel = (typeof formNivelOptions)[number];

/**
 * Niveles que se auto-confirman al inscribirse como Asistente, mientras haya
 * cupo en los días elegidos. "Otro" (texto libre) NUNCA auto-confirma, aunque
 * el texto diga "gerente": la decisión queda en manos del equipo vía admin.
 */
export const nivelesEjecutivos = [
  "Gerente General / CEO",
  "Director(a)",
  "Gerente",
  "Subgerente",
] as const satisfies readonly FormNivel[];

/** Opción que dispara un campo de texto abierto para el área. */
export const AREA_OTRO = "Otro";

/** Opciones del dropdown "Área a la que pertenece". */
export const formAreaOptions = [
  "Recursos Humanos",
  "Tecnología de la Información (TI)",
  "Innovación",
  "Finanzas",
  "Gerencia General",
  "Operaciones",
  "Comercial / Ventas",
  "Marketing",
  "Transformación Digital",
  AREA_OTRO,
] as const;
export type FormArea = (typeof formAreaOptions)[number];

export const formBenefits = [
  { dot: "var(--color-mint-500)", txt: "Prioridad para ser considerado en la primera ola de invitaciones." },
  { dot: "var(--color-blue-300)", txt: "Acceso anticipado al programa y line-up de speakers." },
  { dot: "var(--color-pink-500)", txt: "Actualizaciones y contenidos exclusivos sobre Impacta IA." },
] as const;

/**
 * Footer link `intent` flags the Footer to render this entry as a button
 * that pre-selects a form interest before scrolling — surfaces a clearer
 * path for sponsors / media reaching out.
 */
export type FooterLink = {
  readonly t: string;
  readonly a: string;
  readonly intent?: FormInteres;
};

export const footerColumns: ReadonlyArray<{ readonly h: string; readonly l: ReadonlyArray<FooterLink> }> = [
  {
    h: "El evento",
    l: [
      { t: "Pre-regístrate", a: "/#form" },
      { t: "Los 3 ejes",     a: "/#ejes" },
      { t: "Para quién",     a: "/#para-quien" },
      { t: "Quiero ser sponsor",  a: "/#form", intent: "Sponsor" },
      { t: "Soy media",           a: "/#form", intent: "Media" },
    ],
  },
  {
    h: "Contacto",
    l: [
      { t: event.contact.general,  a: `mailto:${event.contact.general}` },
      { t: event.contact.sponsors, a: `mailto:${event.contact.sponsors}` },
      { t: event.contact.press,    a: `mailto:${event.contact.press}` },
    ],
  },
  {
    h: "Síguenos",
    l: [
      { t: "LinkedIn",  a: "https://www.linkedin.com/company/brincaglobal/" },
      { t: "Instagram", a: "https://www.instagram.com/brinca.global/" },
    ],
  },
];
